import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CheckCircle2, Save, Search } from 'lucide-react';
import { complianceTasksAPI, productsAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import { isAllStoresSelection } from '../BI/useBiVendorId';

const formatLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeText = (value) => String(value ?? '').trim();
const normalizeKey = (value) => normalizeText(value).toLowerCase();

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const DailyLiquorCount = () => {
  const { hasFeatureAccess } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const selectedStoreContext = useMemo(() => ({
    storeInfoId: selectedStore?.storeInfoId || selectedStore?.store_info_id || null,
    vendorId: selectedStore?.vendorId || selectedStore?.vendor_id || null,
    legacyStoreId: selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || null,
    storeName: selectedStore?.name || selectedStore?.storeName || selectedStore?.storeCode || '',
  }), [selectedStore]);
  const hasStoreContext = Boolean(
    !isAllStoresSelection(selectedStore)
    && selectedStoreContext.storeInfoId
    && selectedStoreContext.vendorId
  );
  const selectedStoreLogId = selectedStoreContext.storeInfoId || selectedStoreContext.legacyStoreId || '';
  const canViewPage = hasFeatureAccess('daily_liquor_count.task');

  const [taskDate, setTaskDate] = useState(formatLocalDateKey());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const loadPageData = async () => {
    if (!canViewPage || !hasStoreContext) return;

    setLoading(true);
    try {
      const loadStoreProducts = async () => {
        const response = await productsAPI.getClassified({
          classificationCode: 'liquor',
          storeInfoId: selectedStoreContext.storeInfoId,
          vendorId: selectedStoreContext.vendorId,
          limit: 5000,
        });
        return response.data?.data || [];
      };

      const [allProductRows, logsResponse] = await Promise.all([
        loadStoreProducts(),
        complianceTasksAPI.getLogs({
          date: taskDate,
          storeId: selectedStoreLogId,
          taskType: 'dailyLiquorCount',
        }),
      ]);

      const allProducts = (allProductRows || []).map((product) => ({
        ...product,
        id: product.store_product_id || product.storeProductId || product.id || product.sku,
        storeProductId: product.store_product_id || product.storeProductId || product.id || null,
        sellerId: product.seller_id ?? product.sellerId ?? null,
        sku: normalizeText(product.sku),
        name: normalizeText(
          product.name
          || product.product
          || product.product_name
          || product.Product
          || product.Name
          || product.sku
        ),
        sellerName: normalizeText(
          product.seller_name
          || product.seller
          || product.sellerName
          || product.Seller
        ),
        barcode: normalizeText(product.barcode || product.BarCode || product.bar_code || product.ean || product.EAN),
        stockQuantity: Number(product.stock_quantity ?? product.stock_qty ?? product.stockQuantity ?? 0) || 0,
      }));

      const liquorProducts = allProducts
        .filter((product) => Boolean(product.id))
        .filter((product) => product.is_active !== false)
        .sort((left, right) => left.name.localeCompare(right.name));

      const latestLogByKey = new Map();
      (logsResponse.data?.data || []).forEach((log) => {
        const keys = [
          normalizeKey(log?.subtask_id),
          normalizeKey(log?.product_sku),
        ].filter(Boolean);
        if (!keys.length) return;

        const data = log?.data && typeof log.data === 'object' && !Array.isArray(log.data) ? log.data : {};
        const count = toNumberOrNull(data.count ?? data.quantity ?? data.totalQuantity);
        if (count === null) return;

        const timestamp = new Date(log?.updated_at || log?.completed_at || log?.created_at || 0).getTime();
        keys.forEach((key) => {
          const existing = latestLogByKey.get(key);
          const existingTimestamp = existing?.timestamp ?? -1;
          if (!existing || timestamp >= existingTimestamp) {
            latestLogByKey.set(key, { count, timestamp });
          }
        });
      });

      const nextRows = liquorProducts.map((product) => {
        const idKey = normalizeKey(product.id);
        const skuKey = normalizeKey(product.sku);
        const matched = latestLogByKey.get(idKey) || latestLogByKey.get(skuKey) || null;

        return {
          id: product.id,
          storeProductId: product.storeProductId || product.id,
          sellerId: product.sellerId,
          sku: product.sku || '-',
          barcode: product.barcode || '-',
          name: product.name || '-',
          sellerName: product.sellerName || '-',
          count: matched ? String(matched.count) : '',
        };
      });

      setRows(nextRows);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load daily liquor count data');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canViewPage || !hasStoreContext) return;
    loadPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewPage, hasStoreContext, selectedStoreContext.storeInfoId, selectedStoreContext.vendorId, taskDate]);

  useEffect(() => {
    if (!canViewPage) return undefined;

    const interval = setInterval(() => {
      const nextDate = formatLocalDateKey();
      setTaskDate((currentDate) => {
        if (currentDate === nextDate) return currentDate;
        toast.success('New day started. Daily liquor count has been refreshed.');
        return nextDate;
      });
    }, 60000);

    return () => clearInterval(interval);
  }, [canViewPage]);

  const filteredRows = useMemo(() => {
    const query = normalizeKey(search);
    if (!query) return rows;

    return rows.filter((row) => (
      normalizeKey(row.name).includes(query)
      || normalizeKey(row.sku).includes(query)
      || normalizeKey(row.barcode).includes(query)
      || normalizeKey(row.sellerName).includes(query)
    ));
  }, [rows, search]);

  const enteredCount = useMemo(
    () => rows.filter((row) => row.count !== '').length,
    [rows]
  );

  const updateCount = (rowId, value) => {
    const normalizedValue = String(value || '').replace(/[^0-9]/g, '');
    setRows((currentRows) => currentRows.map((row) => (
      row.id === rowId ? { ...row, count: normalizedValue } : row
    )));
  };

  const saveCounts = async () => {
    const rowsToSave = rows.filter((row) => row.count !== '');
    if (!rowsToSave.length) {
      toast.error('Enter at least one liquor count before saving.');
      return;
    }

    setSaving(true);
    try {
      await Promise.all(rowsToSave.map((row) => complianceTasksAPI.logTask({
        taskType: 'dailyLiquorCount',
        taskDate,
        taskId: 'dailyLiquorCount',
        subtaskId: row.storeProductId || row.id,
        subtaskTotal: 1,
        subtaskCompleted: 1,
        productId: row.storeProductId || row.id,
        storeProductId: row.storeProductId || row.id,
        sellerId: row.sellerId,
        productSku: row.sku === '-' ? null : row.sku,
        productName: row.name,
        data: {
          count: Number(row.count),
          storeProductId: row.storeProductId || row.id,
          sellerId: row.sellerId,
        },
      })));

      toast.success('Daily liquor count saved.');
      await loadPageData();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save daily liquor count');
    } finally {
      setSaving(false);
    }
  };

  if (!canViewPage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Daily Liquor Count</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  if (!hasStoreContext) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Daily Liquor Count</h1>
        <div className="card">
          <p className="text-gray-500">Select a store from the header to load liquor count.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Daily Liquor Count</h1>
        <p className="mt-1 text-sm text-gray-600">
          Update all liquor product counts for <span className="font-semibold">{taskDate}</span>. Data resets each new day.
        </p>
        <p className="mt-1 text-xs font-medium text-slate-500">
          Store: {selectedStoreContext.storeName || `Vendor ${selectedStoreContext.vendorId}`}
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card border border-slate-200 bg-slate-50">
          <p className="text-sm font-medium text-slate-700">Liquor Products</p>
          <p className="text-2xl font-bold text-slate-900">{rows.length}</p>
        </div>
        <div className="card border border-emerald-200 bg-emerald-50">
          <p className="text-sm font-medium text-emerald-800">Counts Entered</p>
          <p className="text-2xl font-bold text-emerald-700">{enteredCount}</p>
        </div>
        <div className="card border border-blue-200 bg-blue-50">
          <p className="text-sm font-medium text-blue-800">Pending</p>
          <p className="text-2xl font-bold text-blue-700">{Math.max(0, rows.length - enteredCount)}</p>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-wrap items-end gap-3">
          <label className="relative block min-w-[250px] flex-[1_1_320px]">
            <span className="label">Search</span>
            <Search className="pointer-events-none absolute left-3 top-[2.55rem] h-4 w-4 text-gray-400" />
            <input
              className="input pl-10"
              placeholder="SKU, barcode, product, seller..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>

          <button
            type="button"
            onClick={saveCounts}
            disabled={saving || loading}
            className="btn-primary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? <CheckCircle2 className="h-4 w-4" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving...' : 'Save Counts'}
          </button>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="table-scroll-shell">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3 font-semibold">SKU</th>
                <th className="px-4 py-3 font-semibold">Barcode</th>
                <th className="px-4 py-3 font-semibold">Product</th>
                <th className="px-4 py-3 font-semibold">Seller</th>
                <th className="px-4 py-3 font-semibold text-right">Count</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    Loading liquor products...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    No liquor products found.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-3 font-mono text-gray-600">{row.sku}</td>
                    <td className="px-4 py-3 font-mono text-gray-600">{row.barcode}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{row.name}</td>
                    <td className="px-4 py-3 text-gray-700">{row.sellerName}</td>
                    <td className="px-4 py-3 text-right">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={row.count}
                        placeholder="0"
                        onChange={(event) => updateCount(row.id, event.target.value)}
                        className="input ml-auto w-28 text-right font-mono tabular-nums"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DailyLiquorCount;
